/**
 * Centralized Sentinel content data — domains, industries, downloads, and
 * analytics summaries. These serve as the seeded content layer that Wix
 * Data Collections override when configured.
 */

export interface Domain {
  id: string;
  name: string;
  overview: string;
  safetyMeasures: string[];
  interventionTemplates: string[];
  diagnosticProfiles: string[];
  trainingModules: string[];
  certificationRequirements: string[];
  operatorRoles: string[];
  oemPathways: string[];
}

export interface Industry {
  id: string;
  name: string;
  description: string;
  templates: string[];
  trainingTracks: string[];
  certificationTracks: string[];
  licensingTiers: string[];
}

export interface DownloadResource {
  id: string;
  title: string;
  description: string;
  format: "PDF" | "PPTX";
  category: string;
}

export const DOMAINS: Domain[] = [
  {
    id: "operational-safety",
    name: "Operational Safety",
    overview:
      "Covers process safety, human-factor monitoring, procedure compliance, and autonomous intervention for operational hazards across facilities and workflows.",
    safetyMeasures: [
      "Process safety management compliance",
      "Human-factor and procedure compliance monitoring",
      "Operational hazard threshold enforcement",
      "Workflow anomaly detection",
    ],
    interventionTemplates: [
      "Process halt intervention",
      "Procedure enforcement",
      "Operator fatigue alert",
      "Workflow anomaly containment",
    ],
    diagnosticProfiles: [
      "Process drift detection",
      "Procedure deviation classification",
      "Human-factor stress correlation",
      "Workflow bottleneck prediction",
    ],
    trainingModules: [
      "Operational Safety Fundamentals",
      "Process Intervention Operations",
      "Procedure Compliance Monitoring",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Operational Safety Domain Credential",
      "Process safety awareness module",
    ],
    operatorRoles: ["Process Safety Operator", "Operations Supervisor", "Compliance Monitor"],
    oemPathways: [
      "MES bridge integration",
      "Operations platform SDK adapter",
      "Process partner co-branded certification",
    ],
  },
  {
    id: "environmental-safety",
    name: "Environmental Safety",
    overview:
      "Covers air quality, water quality, emissions, and environmental hazard monitoring with autonomous containment and alert intervention.",
    safetyMeasures: [
      "EPA and environmental compliance",
      "Air and water quality threshold monitoring",
      "Emissions tracking and enforcement",
      "Environmental hazard containment",
    ],
    interventionTemplates: [
      "Emissions reduction intervention",
      "Containment area lockdown",
      "Water quality alert and shutoff",
      "Air quality evacuation advisory",
    ],
    diagnosticProfiles: [
      "Emissions drift detection",
      "Water quality anomaly classification",
      "Air quality trend prediction",
      "Environmental sensor correlation",
    ],
    trainingModules: [
      "Environmental Safety Fundamentals",
      "Containment Intervention Operations",
      "EPA Compliance",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Environmental Safety Domain Credential",
      "EPA awareness module",
    ],
    operatorRoles: ["Environmental Safety Operator", "Environmental Engineer", "Compliance Officer"],
    oemPathways: [
      "Environmental sensor bridge integration",
      "Emissions monitoring SDK adapter",
      "Environmental partner co-branded certification",
    ],
  },
  {
    id: "fire-safety",
    name: "Fire Safety",
    overview:
      "Covers fire detection, suppression systems, thermal monitoring, and autonomous fire intervention across facilities and assets.",
    safetyMeasures: [
      "NFPA compliance",
      "Fire detection system validation",
      "Suppression system readiness monitoring",
      "Thermal threshold enforcement",
    ],
    interventionTemplates: [
      "Suppression system activation",
      "Area evacuation",
      "Ventilation control",
      "Fire barrier deployment",
    ],
    diagnosticProfiles: [
      "Thermal anomaly detection",
      "Smoke and gas correlation",
      "Suppression system readiness",
      "Fire spread prediction",
    ],
    trainingModules: [
      "Fire Safety Fundamentals",
      "Suppression Intervention Operations",
      "NFPA Compliance",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Fire Safety Domain Credential",
      "NFPA awareness module",
    ],
    operatorRoles: ["Fire Safety Operator", "Fire Protection Engineer", "Safety Officer"],
    oemPathways: [
      "Fire panel bridge integration",
      "Suppression system SDK adapter",
      "Fire safety partner co-branded certification",
    ],
  },
  {
    id: "mechanical-safety",
    name: "Mechanical Safety",
    overview:
      "Covers rotating equipment, pressure systems, mechanical integrity, and autonomous intervention for mechanical failures.",
    safetyMeasures: [
      "ASME and mechanical integrity compliance",
      "Pressure and vibration threshold monitoring",
      "Rotating equipment health validation",
      "Mechanical failure prevention",
    ],
    interventionTemplates: [
      "Equipment shutdown intervention",
      "Pressure relief activation",
      "Speed reduction",
      "Mechanical lockout",
    ],
    diagnosticProfiles: [
      "Vibration anomaly classification",
      "Pressure trend prediction",
      "Bearing wear detection",
      "Mechanical fatigue prediction",
    ],
    trainingModules: [
      "Mechanical Safety Fundamentals",
      "Equipment Intervention Operations",
      "ASME Compliance",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Mechanical Safety Domain Credential",
      "ASME awareness module",
    ],
    operatorRoles: ["Mechanical Safety Operator", "Maintenance Engineer", "Reliability Engineer"],
    oemPathways: [
      "CMMS bridge integration",
      "Mechanical sensor SDK adapter",
      "Equipment OEM co-branded certification",
    ],
  },
  {
    id: "electrical-safety",
    name: "Electrical Safety",
    overview:
      "Covers electrical system monitoring, arc flash prevention, grid protection, and autonomous intervention for electrical hazards.",
    safetyMeasures: [
      "NFPA 70E electrical safety compliance",
      "Arc flash threshold monitoring",
      "Electrical system protection validation",
      "Ground fault detection",
    ],
    interventionTemplates: [
      "Circuit isolation intervention",
      "Arc flash prevention activation",
      "Load shedding",
      "Ground fault isolation",
    ],
    diagnosticProfiles: [
      "Electrical load anomaly detection",
      "Arc flash risk prediction",
      "Insulation degradation monitoring",
      "Ground fault classification",
    ],
    trainingModules: [
      "Electrical Safety Fundamentals",
      "Electrical Intervention Operations",
      "NFPA 70E Compliance",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Electrical Safety Domain Credential",
      "NFPA 70E awareness module",
    ],
    operatorRoles: ["Electrical Safety Operator", "Electrical Engineer", "Power Systems Technician"],
    oemPathways: [
      "SCADA bridge integration",
      "Electrical monitoring SDK adapter",
      "Electrical OEM co-branded certification",
    ],
  },
  {
    id: "thermal-safety",
    name: "Thermal Safety",
    overview:
      "Covers thermal monitoring, heat stress prevention, thermal runaway detection, and autonomous intervention for thermal hazards.",
    safetyMeasures: [
      "Thermal threshold compliance",
      "Heat stress monitoring",
      "Thermal runaway detection",
      "Thermal system validation",
    ],
    interventionTemplates: [
      "Thermal runaway containment",
      "Cooling system activation",
      "Heat stress evacuation",
      "Thermal load reduction",
    ],
    diagnosticProfiles: [
      "Thermal trend prediction",
      "Heat stress correlation",
      "Cooling system degradation",
      "Thermal anomaly classification",
    ],
    trainingModules: [
      "Thermal Safety Fundamentals",
      "Thermal Intervention Operations",
      "Thermal Compliance",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Thermal Safety Domain Credential",
      "Thermal safety awareness module",
    ],
    operatorRoles: ["Thermal Safety Operator", "Thermal Engineer", "Process Technician"],
    oemPathways: [
      "Thermal sensor bridge integration",
      "Cooling system SDK adapter",
      "Thermal partner co-branded certification",
    ],
  },
  {
    id: "digital-safety",
    name: "Digital Safety",
    overview:
      "Covers cybersecurity, data integrity, network monitoring, and autonomous intervention for digital threats and cyber incidents.",
    safetyMeasures: [
      "NIST cybersecurity framework compliance",
      "Network intrusion detection",
      "Data integrity monitoring",
      "Digital threat assessment",
    ],
    interventionTemplates: [
      "Network isolation intervention",
      "Threat containment",
      "Access revocation",
      "System lockdown",
    ],
    diagnosticProfiles: [
      "Network anomaly detection",
      "Intrusion pattern classification",
      "Data integrity drift",
      "Threat correlation analysis",
    ],
    trainingModules: [
      "Digital Safety Fundamentals",
      "Cyber Intervention Operations",
      "NIST Compliance",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Digital Safety Domain Credential",
      "NIST awareness module",
    ],
    operatorRoles: ["Digital Safety Operator", "Cybersecurity Analyst", "SOC Engineer"],
    oemPathways: [
      "SIEM bridge integration",
      "Security platform SDK adapter",
      "Cybersecurity partner co-branded certification",
    ],
  },
  {
    id: "chemical-safety",
    name: "Chemical Safety",
    overview:
      "Covers chemical storage, handling, exposure monitoring, and autonomous intervention for chemical hazards and release events.",
    safetyMeasures: [
      "OSHA and REACH chemical safety compliance",
      "Chemical exposure threshold monitoring",
      "Storage integrity validation",
      "Chemical reaction hazard assessment",
    ],
    interventionTemplates: [
      "Chemical release containment",
      "Exposure evacuation",
      "Ventilation activation",
      "Storage isolation",
    ],
    diagnosticProfiles: [
      "Chemical sensor anomaly detection",
      "Exposure trend prediction",
      "Storage integrity monitoring",
      "Reaction hazard classification",
    ],
    trainingModules: [
      "Chemical Safety Fundamentals",
      "Containment Intervention Operations",
      "OSHA Chemical Compliance",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Chemical Safety Domain Credential",
      "OSHA chemical awareness module",
    ],
    operatorRoles: ["Chemical Safety Operator", "Chemical Engineer", "Hazmat Specialist"],
    oemPathways: [
      "Chemical sensor bridge integration",
      "Handling system SDK adapter",
      "Chemical partner co-branded certification",
    ],
  },
  {
    id: "compliance-governance",
    name: "Compliance & Governance",
    overview:
      "Covers regulatory compliance, audit trails, policy enforcement, and autonomous governance intervention across all safety domains.",
    safetyMeasures: [
      "Regulatory framework compliance",
      "Audit trail integrity monitoring",
      "Policy enforcement validation",
      "Governance risk assessment",
    ],
    interventionTemplates: [
      "Compliance enforcement",
      "Audit trail preservation",
      "Policy violation alert",
      "Governance escalation",
    ],
    diagnosticProfiles: [
      "Compliance drift detection",
      "Audit anomaly classification",
      "Policy deviation prediction",
      "Governance risk correlation",
    ],
    trainingModules: [
      "Compliance & Governance Fundamentals",
      "Governance Intervention Operations",
      "Regulatory Framework Compliance",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Compliance & Governance Domain Credential",
      "Regulatory compliance awareness module",
    ],
    operatorRoles: ["Compliance Officer", "Governance Analyst", "Audit Coordinator"],
    oemPathways: [
      "GRC platform bridge integration",
      "Compliance SDK adapter",
      "Governance partner co-branded certification",
    ],
  },
  {
    id: "radiation-safety",
    name: "Radiation Safety",
    overview:
      "Covers radiation monitoring, dosimetry, source containment, and autonomous intervention for radiation exposure and shielding events.",
    safetyMeasures: [
      "Radiation safety standards compliance",
      "Dosimetry threshold monitoring",
      "Source containment validation",
      "Shielding integrity assessment",
    ],
    interventionTemplates: [
      "Dose threshold auto-alert",
      "Source leak containment",
      "Shielding degradation response",
      "Area evacuation",
    ],
    diagnosticProfiles: [
      "Radiation source anomaly detection",
      "Dose trend prediction",
      "Shielding degradation monitoring",
      "Cross-facility radiation correlation",
    ],
    trainingModules: [
      "Radiation Safety Fundamentals",
      "Source & Shielding Operations",
      "Cross-Facility Radiation Management",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Radiation Safety Domain Credential",
      "Radiation safety awareness module",
    ],
    operatorRoles: ["Radiation Safety Operator", "Health Physicist", "Radiation Safety Officer"],
    oemPathways: [
      "Dosimetry bridge integration",
      "Radiation monitoring SDK adapter",
      "Radiation partner co-branded certification",
    ],
  },
  {
    id: "biological-safety",
    name: "Biological Safety",
    overview:
      "Covers biohazard detection, containment, exposure monitoring, and autonomous intervention for biological threats and contamination events.",
    safetyMeasures: [
      "Biosafety level compliance",
      "Bio-aerosol and contamination monitoring",
      "Containment integrity validation",
      "Exposure risk assessment",
    ],
    interventionTemplates: [
      "Bio-aerosol auto-containment",
      "Contamination event response",
      "Exposure prevention evacuation",
      "Facility lockdown",
    ],
    diagnosticProfiles: [
      "Biosensor anomaly detection",
      "Contamination trend prediction",
      "Containment integrity monitoring",
      "Cross-domain bio-environmental correlation",
    ],
    trainingModules: [
      "Biological Safety Fundamentals",
      "Containment & Decontamination Operations",
      "Cross-Domain Bio-Environmental Management",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Biological Safety Domain Credential",
      "Biosafety awareness module",
    ],
    operatorRoles: ["Biological Safety Operator", "Biosafety Officer", "Microbiology Technician"],
    oemPathways: [
      "Biosensor bridge integration",
      "Containment system SDK adapter",
      "Biosafety partner co-branded certification",
    ],
  },
  {
    id: "structural-safety",
    name: "Structural Safety",
    overview:
      "Covers structural health monitoring, fatigue detection, load analysis, and autonomous intervention for structural failure prevention.",
    safetyMeasures: [
      "Structural integrity standards compliance",
      "Fatigue and crack monitoring",
      "Load threshold enforcement",
      "Displacement and settlement tracking",
    ],
    interventionTemplates: [
      "Fatigue and crack detection alert",
      "Load redistribution intervention",
      "Access restriction",
      "Evacuation advisory",
    ],
    diagnosticProfiles: [
      "Strain and vibration anomaly detection",
      "Crack initiation prediction",
      "Load redistribution monitoring",
      "Displacement and settlement correlation",
    ],
    trainingModules: [
      "Structural Safety Fundamentals",
      "Fatigue & Load Intervention Operations",
      "Cross-Domain Structural Management",
    ],
    certificationRequirements: [
      "Certified Sentinel Operator",
      "Structural Safety Domain Credential",
      "Structural safety awareness module",
    ],
    operatorRoles: ["Structural Safety Operator", "Structural Engineer", "Infrastructure Inspector"],
    oemPathways: [
      "SHM bridge integration",
      "Structural sensor SDK adapter",
      "Infrastructure partner co-branded certification",
    ],
  },
];

export const INDUSTRIES: Industry[] = [
  {
    id: "defense-military",
    name: "Defense & Military",
    description:
      "Mission-critical platform safety, autonomous engagement rules, and classified-domain deployment for defense operations.",
    templates: ["Platform Safety", "Engagement Rules", "Survivability", "Classified Domains"],
    trainingTracks: ["Defense Safety Fundamentals", "Platform Intervention", "MIL-STD-882E"],
    certificationTracks: ["Defense Domain Credential", "Platform Safety Certification"],
    licensingTiers: ["Tier 4 — Government & Defense License"],
  },
  {
    id: "aerospace-aviation",
    name: "Aerospace & Aviation",
    description:
      "Flight-critical monitoring, avionics redundancy, and structural health prediction for commercial and defense aviation.",
    templates: ["Avionics Safety", "Structural Health", "Engine Performance", "Flight Safety"],
    trainingTracks: ["Aviation Safety", "Avionics Intervention", "DO-178C"],
    certificationTracks: ["Aviation Domain Credential", "Avionics Certification"],
    licensingTiers: ["Tier 3 — OEM/Tier-1 License", "Tier 4 — Government & Defense License"],
  },
  {
    id: "automotive-mobility",
    name: "Automotive & Mobility",
    description:
      "ADAS validation, braking intervention, and autonomous fallback for passenger, commercial, and autonomous vehicles.",
    templates: ["ADAS Safety", "Braking Systems", "Powertrain Safety", "Autonomous Fallback"],
    trainingTracks: ["Automotive Safety", "ADAS Intervention", "ISO 26262"],
    certificationTracks: ["Automotive Domain Credential", "ADAS Certification"],
    licensingTiers: ["Tier 2 — Organization License", "Tier 3 — OEM/Tier-1 License"],
  },
  {
    id: "energy-utilities",
    name: "Energy & Utilities",
    description:
      "Grid stability, generation health, and distributed resource coordination for utilities and energy operators.",
    templates: ["Grid Stability", "Generation Health", "Load Management", "Fault Isolation"],
    trainingTracks: ["Energy Safety", "Grid Intervention", "NERC CIP"],
    certificationTracks: ["Energy Domain Credential", "Grid Operations Certification"],
    licensingTiers: ["Tier 2 — Organization License", "Tier 4 — Government & Defense License"],
  },
  {
    id: "manufacturing-industrial",
    name: "Manufacturing & Industrial Automation",
    description:
      "Industrial robot safety, production line monitoring, and predictive maintenance for smart manufacturing.",
    templates: ["Robot Safety", "Production Line", "Predictive Maintenance", "Cell Safety"],
    trainingTracks: ["Robotics Safety", "Automation Intervention", "ISO 10218"],
    certificationTracks: ["Robotics Domain Credential", "Automation Certification"],
    licensingTiers: ["Tier 2 — Organization License", "Tier 3 — OEM/Tier-1 License"],
  },
  {
    id: "smart-cities",
    name: "Smart Cities",
    description:
      "Urban infrastructure monitoring, traffic safety, and emergency response coordination for municipal operators.",
    templates: ["Infrastructure Safety", "Traffic Systems", "Emergency Response", "Environmental"],
    trainingTracks: ["Infrastructure Safety", "Urban Intervention", "Emergency Coordination"],
    certificationTracks: ["Infrastructure Domain Credential"],
    licensingTiers: ["Tier 2 — Organization License", "Tier 4 — Government & Defense License"],
  },
  {
    id: "medical-devices",
    name: "Medical Devices",
    description:
      "Patient-safety monitoring, therapeutic intervention, and device interoperability for medical device manufacturers.",
    templates: ["Patient Safety", "Therapeutic Intervention", "Device Interoperability"],
    trainingTracks: ["Medical Device Safety", "Therapeutic Intervention", "IEC 62304"],
    certificationTracks: ["Medical Device Domain Credential"],
    licensingTiers: ["Tier 3 — OEM/Tier-1 License"],
  },
  {
    id: "rail-transit",
    name: "Rail & Transit",
    description:
      "Signaling safety, rolling stock health, and corridor-level intervention for rail and transit operators.",
    templates: ["Signaling Safety", "Rolling Stock", "Corridor Management", "Track Integrity"],
    trainingTracks: ["Rail Safety", "Signaling Intervention", "EN 50128"],
    certificationTracks: ["Rail Domain Credential"],
    licensingTiers: ["Tier 2 — Organization License", "Tier 4 — Government & Defense License"],
  },
  {
    id: "maritime",
    name: "Maritime",
    description:
      "Vessel propulsion, navigation safety, and cargo integrity monitoring for maritime and port operations.",
    templates: ["Propulsion Safety", "Navigation", "Cargo Integrity", "Port Operations"],
    trainingTracks: ["Maritime Safety", "Navigation Intervention", "SOLAS"],
    certificationTracks: ["Maritime Domain Credential"],
    licensingTiers: ["Tier 2 — Organization License", "Tier 4 — Government & Defense License"],
  },
];

export const DOWNLOADS: DownloadResource[] = [
  {
    id: "dl-architecture",
    title: "Architecture Overview",
    description: "Detailed breakdown of the four-layer Sentinel architecture and data flow.",
    format: "PDF",
    category: "Architecture",
  },
  {
    id: "dl-domain-templates",
    title: "Domain Overview Summary",
    description: "High-level overview of Sentinel’s twelve safety domains and how each applies the multi-domain architecture to real-world environments.",
    format: "PDF",
    category: "Domains",
  },
  {
    id: "dl-intervention-libraries",
    title: "Intervention Framework Overview",
    description: "Conceptual summary of Sentinel’s intervention philosophy, illustrating how corrective actions and safety responses are coordinated across domains.",
    format: "PDF",
    category: "Intervention",
  },
  {
    id: "dl-oem-integration",
    title: "OEM/Tier-1 Integration Overview",
    description: "General overview of Sentinel’s integration approach for OEMs and Tier-1 suppliers, describing supported interfaces and partnership pathways.",
    format: "PDF",
    category: "Integration",
  },
  {
    id: "dl-safety-analytics",
    title: "Safety Analytics Overview",
    description: "Overview of Sentinel's analytics capabilities and dashboard metrics.",
    format: "PDF",
    category: "Analytics",
  },
  {
    id: "dl-certification",
    title: "Certification Requirements",
    description: "Full requirements for operator, domain, OEM, and compliance certifications.",
    format: "PDF",
    category: "Certification",
  },
  {
    id: "dl-training-catalog",
    title: "Training Catalog",
    description: "Complete catalog of core and advanced training modules and programs.",
    format: "PDF",
    category: "Training",
  },
  {
    id: "dl-pitch-deck",
    title: "Sentinel Pitch Deck",
    description: "Executive pitch deck covering value proposition, architecture, and market.",
    format: "PPTX",
    category: "Executive",
  },
];

export interface AnalyticsSummary {
  domain: string;
  interventionsExecuted: number;
  diagnosticAccuracy: number;
  meanTimeToDetect: string;
  meanTimeToIntervene: string;
  operatorPerformance: number;
  certificationProgress: number;
  complianceScore: number;
}

export const ANALYTICS_SUMMARIES: AnalyticsSummary[] = [
  {
    domain: "Automotive",
    interventionsExecuted: 48230,
    diagnosticAccuracy: 99.2,
    meanTimeToDetect: "42ms",
    meanTimeToIntervene: "180ms",
    operatorPerformance: 94,
    certificationProgress: 78,
    complianceScore: 96,
  },
  {
    domain: "Aviation",
    interventionsExecuted: 12450,
    diagnosticAccuracy: 99.7,
    meanTimeToDetect: "38ms",
    meanTimeToIntervene: "150ms",
    operatorPerformance: 97,
    certificationProgress: 82,
    complianceScore: 99,
  },
  {
    domain: "Energy",
    interventionsExecuted: 33100,
    diagnosticAccuracy: 98.9,
    meanTimeToDetect: "55ms",
    meanTimeToIntervene: "220ms",
    operatorPerformance: 91,
    certificationProgress: 71,
    complianceScore: 94,
  },
  {
    domain: "Defense",
    interventionsExecuted: 8900,
    diagnosticAccuracy: 99.5,
    meanTimeToDetect: "30ms",
    meanTimeToIntervene: "120ms",
    operatorPerformance: 98,
    certificationProgress: 85,
    complianceScore: 100,
  },
  {
    domain: "Robotics",
    interventionsExecuted: 27600,
    diagnosticAccuracy: 99.0,
    meanTimeToDetect: "45ms",
    meanTimeToIntervene: "160ms",
    operatorPerformance: 93,
    certificationProgress: 74,
    complianceScore: 95,
  },
  {
    domain: "Maritime",
    interventionsExecuted: 6200,
    diagnosticAccuracy: 98.5,
    meanTimeToDetect: "60ms",
    meanTimeToIntervene: "240ms",
    operatorPerformance: 89,
    certificationProgress: 68,
    complianceScore: 92,
  },
];
